import React, { useState } from 'react';
import { cn } from 'lib/utils';
import { supabase } from 'config/supabase';

interface SubscriptionInfoProps {
  planName?: string;
  status?: string;
  nextBillingDate?: string;
  outputsUsed?: number;
  outputsLimit?: number;
  loading?: boolean;
  className?: string;
}

const SubscriptionInfo: React.FC<SubscriptionInfoProps> = ({
  planName = 'Free',
  status = 'active',
  nextBillingDate,
  outputsUsed,
  outputsLimit,
  loading = false,
  className,
}) => {
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleManageBilling = async () => {
    try {
      setLoadingPortal(true);
      setError(null);

      const { data, error: functionError } = await supabase.functions.invoke(
        'create-billing-portal-session',
      );

      if (functionError) {
        throw functionError;
      }

      if (!data?.url) {
        throw new Error('No billing portal URL returned');
      }

      // Open Stripe billing portal in new tab
      window.open(data.url, '_blank');

      // Reset loading state since we're not leaving the page
      setLoadingPortal(false);
    } catch (err: any) {
      console.error('Billing portal error:', err);
      setError(err.message || 'Failed to open billing portal');
      setLoadingPortal(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600';
      case 'canceled':
      case 'cancelled':
        return 'text-red-600';
      case 'past_due':
        return 'text-yellow-600';
      case 'trialing':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatPlanName = (plan: string) => {
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  if (loading) {
    return (
      <div className={cn('space-y-3', className)}>
        <div>
          <h3 className="text-black font-medium text-lg mb-2">Subscription / Plan Info</h3>
        </div>
        <div className="animate-pulse space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div>
        <h3 className="text-black font-medium text-lg mb-2">Subscription / Plan Info</h3>
      </div>

      <div className="flex items-center gap-2">
        <p className="text-black font-medium text-sm">Current Plan:</p>
        <span className="text-black font-semibold">{formatPlanName(planName)}</span>
      </div>

      <div className="flex items-center gap-2">
        <p className="text-black font-medium text-sm">Status:</p>
        <span className={cn('font-semibold capitalize', getStatusColor(status))}>{status}</span>
      </div>

      {/* Outputs remaining - show for all plans */}
      <div className="flex items-center gap-2">
        <p className="text-black font-medium text-sm">Outputs:</p>
        {outputsUsed !== undefined && outputsLimit !== undefined ? (
          <span className="text-black font-semibold">
            {outputsUsed} / {outputsLimit} used
            <span className="ml-2 text-gray-500 font-normal">
              ({outputsLimit - outputsUsed} remaining)
            </span>
          </span>
        ) : (
          <span className="text-green-600 font-semibold">Unlimited</span>
        )}
      </div>

      {nextBillingDate && status === 'active' && (
        <div className="flex gap-2">
          <p className="text-black font-medium text-sm">Next billing:</p>
          <p className="text-black text-sm">{nextBillingDate}</p>
        </div>
      )}

      {status === 'canceled' && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
          <p className="text-sm text-red-800">
            Your subscription has been canceled. Access will remain until the current period ends.
          </p>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Manage Billing Link */}
      {planName !== 'Free' && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <button
            onClick={handleManageBilling}
            disabled={loadingPortal}
            className="text-sm text-purple-600 hover:text-purple-700 underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingPortal ? 'Opening billing portal...' : 'Manage billing & subscription'}
          </button>
          <p className="text-xs text-gray-500 mt-1">
            Opens Stripe portal in new tab. Change plan, update payment method, or view invoices.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionInfo;
